import { useEffect, useRef, useState } from 'react';

/**
 * スケルトンローダーの最低表示時間を管理するカスタムフック
 * @param isLoading - ローディング状態
 * @param minDisplayTime - 最低表示時間（ミリ秒、デフォルト: 500ms）
 * @returns 実際にスケルトンを表示すべきかどうか
 */
export const useSkeletonMinDisplay = (isLoading: boolean, minDisplayTime = 500): boolean => {
  const [showSkeleton, setShowSkeleton] = useState(false);
  const loadingStartTimeRef = useRef<number | null>(null);
  const minDisplayTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isLoading) {
      // ローディング開始時刻を記録
      loadingStartTimeRef.current = Date.now();
      setShowSkeleton(true);
      // 既存のタイマーをクリア
      if (minDisplayTimeoutRef.current) {
        clearTimeout(minDisplayTimeoutRef.current);
        minDisplayTimeoutRef.current = null;
      }
    } else {
      // ローディングが終了した場合、最低表示時間を確保
      if (loadingStartTimeRef.current !== null) {
        const elapsed = Date.now() - loadingStartTimeRef.current;
        const remainingTime = Math.max(0, minDisplayTime - elapsed);

        if (remainingTime > 0) {
          // 残り時間がある場合は、その時間だけスケルトンを表示し続ける
          minDisplayTimeoutRef.current = setTimeout(() => {
            setShowSkeleton(false);
            loadingStartTimeRef.current = null;
            minDisplayTimeoutRef.current = null;
          }, remainingTime);
        } else {
          // 既に最低表示時間を超えている場合は即座に非表示
          setShowSkeleton(false);
          loadingStartTimeRef.current = null;
        }
      }
    }

    // クリーンアップ
    return () => {
      if (minDisplayTimeoutRef.current) {
        clearTimeout(minDisplayTimeoutRef.current);
        minDisplayTimeoutRef.current = null;
      }
    };
  }, [isLoading, minDisplayTime]);

  // isLoading が true の場合は即座に true を返す（useEffect の1フレーム遅延を回避）
  // showSkeleton は最低表示時間の保証に使用
  return showSkeleton || isLoading;
};
